// "Mine" on the room's material shelf: the producer's library clips, placed
// into this room. The library itself — folders, tags, stars, files dropped
// on the shelf — is useMineLibrary, shared with the library page.
//
// Placing is placeLibraryClip with the clip's own pool key — no upload, the
// bytes are already stored — and `libraryClip` naming where it came from.
// The privacy rule decides the rest: the LIBRARY is private, a PLACED clip is
// the project's audio. So in a connected room the jam takes a reference to the
// blob before the clip is proposed (POST /jam/:id/files), which is what lets
// every participant fetch it; a local room plays it through the owner's
// signed URL until a save gives it a jam of its own (PublishRoom).

import { useCallback, useState } from "react";

import type { MyClip, MyMidi } from "@shared/api-types.js";
import { hashOfPoolKey } from "@shared/blobs.js";
import type { Intent } from "@shared/sync/intents.js";
import type { CanonicalRoom } from "@shared/sync/state.js";
import { track } from "@web-shared/lib/analytics";
import { createMidiClip, myClipPlacement, newInstrumentLane, nextOrd, placeLibraryClip, placeLibraryClipInSlot, placeOnNewLane, replaceClipAudio } from "@web-shared/lib/edits";
import { referenceMidiOf } from "@web-shared/lib/material-item";
import { firstFreeSlot, snap } from "@web-shared/lib/placement";
import { json, webApi } from "@web-shared/lib/web-api";
import type { MaterialFilter, MineShelf } from "@/components/MaterialLibrary";
import { suggestFromMidi } from "@web-shared/lib/tags/midi-key";
import { useMineLibrary } from "./useMineLibrary";

/** A pool key's channel prefix: whatever precedes `blobs/`. */
function hashOf(key: string): string | null {
  const at = key.indexOf("blobs/");
  return at < 0 ? null : hashOfPoolKey(key, key.slice(0, at));
}

export function useMineShelf({
  room,
  editor,
  tempo,
  jamId,
  seedId,
  grid,
  playheadBeats,
  selectedLaneId,
}: {
  room: CanonicalRoom | null;
  editor: { propose: (intents: Intent[]) => void };
  tempo: number;
  /** Set in a connected room: the jam that must hold a reference to placed audio. */
  jamId: string | undefined;
  /** The seed this room opened from, when one did (a local room). */
  seedId?: string | undefined;
  grid: number;
  playheadBeats: number;
  selectedLaneId: string | null;
}) {
  // Everything that is the library's own — folders, tags, stars, drops —
  // comes from the hook the library page shares; this adds the room.
  const { mine, library, keep, keepMidi } = useMineLibrary();
  const [filter, setFilter] = useState<MaterialFilter>("project");

  /** The project's audio from here on: in a jam, every participant must be able to fetch it. */
  const shareWithJam = useCallback(
    async (clip: MyClip) => {
      if (!jamId) return;
      const hash = hashOf(clip.audioKey);
      if (!hash) return;
      try {
        await webApi(`/jam/${encodeURIComponent(jamId)}/files`, json("POST", { hash, sizeBytes: clip.sizeBytes }));
      } catch {
        // The placement still lands for this producer; others hear it after a save.
      }
    },
    [jamId],
  );

  /** Put a library clip on a lane (or a new one) at a beat. */
  const place = useCallback(
    async (clip: MyClip, laneId: string | null, atBeat: number) => {
      if (!room) return;
      await shareWithJam(clip);
      const placement = myClipPlacement({ ...clip, durationSec: clip.durationMs / 1000 }, tempo);
      const at = Math.max(0, snap(atBeat, grid));
      const lane = laneId ? room.tracks.find((t) => t.id === laneId) : null;
      const audioLane = lane && (lane.trackKind ?? "audio") === "audio" ? lane : null;
      if (laneId && !audioLane) return; // dropped on a MIDI lane: nothing to place
      if (audioLane) {
        const intent = placeLibraryClip(placement, audioLane.id, at, (start, duration) =>
          firstFreeSlot(room, audioLane.id, start, duration),
        );
        if (intent) editor.propose([intent]);
      } else {
        const ord = room.tracks.reduce((m, t) => Math.max(m, t.ord), -1) + 1;
        const intents = placeOnNewLane(placement, clip.name, ord, at);
        if (intents.length > 0) editor.propose(intents);
      }
      track("library_clip_placed", { detail: clip.kind, value: 1 });
    },
    [room, shareWithJam, tempo, grid, editor],
  );

  /** Put a library clip in a Session View slot — a pad. False when the slot
   * isn't an empty one on an audio lane. */
  const placeInSlot = useCallback(
    async (clip: MyClip, laneId: string, slot: number): Promise<boolean> => {
      if (!room) return false;
      const placement = myClipPlacement({ ...clip, durationSec: clip.durationMs / 1000 }, tempo);
      const intent = placeLibraryClipInSlot(room, placement, laneId, slot);
      if (!intent) return false;
      await shareWithJam(clip);
      editor.propose([intent]);
      track("library_clip_placed", { detail: clip.kind, value: 1 });
      return true;
    },
    [room, tempo, shareWithJam, editor],
  );

  /**
   * ＋ on a card, at the playhead: the selected audio lane; else the lane this
   * clip already went onto (one named after it), so ＋ again stacks it there;
   * else a new lane.
   */
  const placeAtPlayhead = useCallback(
    (clip: MyClip) => {
      const isAudio = (t: { trackKind?: string }): boolean => (t.trackKind ?? "audio") === "audio";
      const selected = selectedLaneId ? room?.tracks.find((t) => t.id === selectedLaneId) : null;
      const named = room?.tracks.find((t) => isAudio(t) && t.name === clip.name);
      const laneId = selected && isAudio(selected) ? selected.id : (named?.id ?? null);
      void place(clip, laneId, playheadBeats);
    },
    [place, room, selectedLaneId, playheadBeats],
  );

  /** A Mine card dropped on a lane (ArrangementView onDropMine). */
  const onDropMine = useCallback(
    (laneId: string, atBeat: number, libraryClipId: string) => {
      const clip = mine.clips.find((c) => c.id === libraryClipId);
      if (clip) void place(clip, laneId, atBeat);
    },
    [mine.clips, place],
  );

  /** A Mine card dropped on an empty pad. */
  const onDropMineSlot = useCallback(
    (laneId: string, slot: number, libraryClipId: string) => {
      const clip = mine.clips.find((c) => c.id === libraryClipId);
      if (clip) void placeInSlot(clip, laneId, slot);
    },
    [mine.clips, placeInSlot],
  );

  /** A Mine card dropped below the lanes: a new audio track with it on. */
  const onDropMineNewLane = useCallback(
    (atBeat: number, libraryClipId: string) => {
      const clip = mine.clips.find((c) => c.id === libraryClipId);
      if (clip) void place(clip, null, atBeat);
    },
    [mine.clips, place],
  );

  // ---- MIDI from the library ------------------------------------------------

  /** ＋ on a MIDI idea: the selected MIDI lane at the playhead, else a lane
   * of its own with a keys instrument (what a MIDI drop with no lane gets). */
  const placeMidiAtPlayhead = useCallback(
    (midi: MyMidi) => {
      if (!room) return;
      const at = Math.max(0, snap(playheadBeats, grid));
      const selected = selectedLaneId ? room.tracks.find((t) => t.id === selectedLaneId) : null;
      if (selected && selected.trackKind === "midi") {
        const intent = createMidiClip(room, selected.id, at, midi.durationBeats, (start, duration) => firstFreeSlot(room, selected.id, start, duration), {
          notes: midi.notes,
          name: midi.name,
        });
        if (intent) editor.propose([intent]);
      } else {
        const made = newInstrumentLane(midi.name, nextOrd(room), "keys", at, midi.durationBeats, midi.notes);
        if (made) editor.propose(made.intents);
      }
      track("library_clip_placed", { detail: "midi", value: 1 });
    },
    [room, playheadBeats, grid, selectedLaneId, editor],
  );

  /** A MIDI idea carried onto a lane: onto it when it is a MIDI lane. */
  const onDropMineMidi = useCallback(
    (laneId: string, atBeat: number, libraryMidiId: string) => {
      const midi = mine.midi.find((m) => m.id === libraryMidiId);
      if (!room || !midi) return;
      const at = Math.max(0, snap(atBeat, grid));
      const intent = createMidiClip(room, laneId, at, midi.durationBeats, (start, duration) => firstFreeSlot(room, laneId, start, duration), {
        notes: midi.notes,
        name: midi.name,
      });
      if (intent) editor.propose([intent]);
    },
    [mine.midi, room, grid, editor],
  );

  /** A MIDI idea let go below the lanes: a new MIDI lane with it on. */
  const onDropMineMidiNewLane = useCallback(
    (atBeat: number, libraryMidiId: string) => {
      const midi = mine.midi.find((m) => m.id === libraryMidiId);
      if (!room || !midi) return;
      const made = newInstrumentLane(midi.name, nextOrd(room), "keys", Math.max(0, snap(atBeat, grid)), midi.durationBeats, midi.notes);
      if (made) editor.propose(made.intents);
    },
    [mine.midi, room, grid, editor],
  );

  /** A MIDI clip of this room, kept: its notes go to the library as an idea. */
  const saveMidi = useCallback(
    (clipId: string, folderId: string | null) => {
      const clip = room?.clips.find((c) => c.id === clipId);
      if (!clip || clip.clipKind !== "midi" || !clip.notes || clip.notes.length === 0) return;
      const lane = room?.tracks.find((t) => t.id === clip.trackId);
      const name = clip.name.trim() || lane?.name.trim() || "MIDI idea";
      let tags: ReturnType<typeof suggestFromMidi> = [];
      try {
        tags = suggestFromMidi(clip.notes);
      } catch {
        tags = [];
      }
      void mine.addMidi({ name, folderId, notes: clip.notes, durationBeats: clip.duration, bpm: tempo > 0 ? tempo : null, ...(tags.length > 0 ? { tags } : {}) });
      track("library_midi_saved", { value: 1 });
    },
    [room, mine, tempo],
  );

  /** ＋ on a seed's clip: kept into Mine first (the bytes are the server's
   * already), then placed as any library clip is. */
  const placeReference = useCallback(
    (refSeedId: string, audioKey: string) => {
      const midiKey = referenceMidiOf(audioKey);
      if (midiKey !== null) {
        void keepMidi(refSeedId, midiKey, null).then((midi) => {
          if (midi) placeMidiAtPlayhead(midi);
        });
        return;
      }
      void keep({ seedId: refSeedId, audioKey, folderId: null }).then((clip) => {
        if (clip) placeAtPlayhead(clip);
      });
    },
    [keep, keepMidi, placeAtPlayhead, placeMidiAtPlayhead],
  );

  /** An audio clip of this room, kept: the jam (or the seed) vouches for the bytes. */
  const saveAudio = useCallback(
    (clipId: string, folderId: string | null) => {
      const clip = room?.clips.find((c) => c.id === clipId);
      if (!clip || clip.clipKind === "midi" || clip.audioKey === null) return;
      const lane = room?.tracks.find((t) => t.id === clip.trackId);
      const name = clip.name.trim() || lane?.name.trim() || "Clip";
      const kind = clip.looping || clip.duration >= 4 ? "loop" : "oneshot";
      const where = jamId ? { jamId } : seedId ? { seedId } : null;
      if (!where) return;
      void keep({ ...where, audioKey: clip.audioKey, folderId, name, kind, bpm: tempo > 0 ? tempo : null });
    },
    [room, jamId, seedId, keep, tempo],
  );

  /** ⇄: the library clip's sound in place of the clip being edited — same
   * geometry, new object, so every listener rebuilds rather than re-places. */
  const swapAudio = useCallback(
    (clipId: string, clip: MyClip) => {
      if (!room) return;
      void shareWithJam(clip).then(() => {
        const intent = replaceClipAudio(room, clipId, clip.audioKey, { name: clip.name });
        if (intent) {
          editor.propose([intent]);
          track("library_swapped", { value: 1 });
        }
      });
    },
    [room, shareWithJam, editor],
  );

  const shelf: MineShelf = {
    ...library,
    onPlace: placeAtPlayhead,
    onPlaceMidi: placeMidiAtPlayhead,
    onSaveMidi: saveMidi,
    onSaveAudio: saveAudio,
    onPlaceReference: placeReference,
  };

  return { shelf, filter, setFilter, onDropMine, onDropMineNewLane, onDropMineMidi, onDropMineMidiNewLane, onDropMineSlot, placeInSlot, place, swapAudio, clips: mine.clips, urlOfKey: mine.urlOfKey };
}
